import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { LuEye, LuEyeOff, LuLock, LuUser } from 'react-icons/lu';
import logo from '../assets/img/logo.png';
import { login } from '../services/loginServices';
import { myRole } from '../services/roleServices';
import { HOME_BY_ROLE } from '../components/Layout/navConfig';
import { getRole, isAuthenticated } from '../utils/session';
import '../styles/login.css';

const validate = ({ username, password }) => {
  const errors = {};
  if (!username.trim()) errors.username = 'Username is required';
  if (!password) errors.password = 'Password is required';
  return errors;
};

function Login() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const sessionExpired = searchParams.get('session') === 'expired';

  // Already signed in → go straight to the role's home page.
  if (isAuthenticated() && HOME_BY_ROLE[getRole()]) {
    return <Navigate to={HOME_BY_ROLE[getRole()]} replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    setErrorMsg('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setLoading(true);
    setErrorMsg('');
    try {
      const response = await login({ ...form, username: form.username.trim() });

      if (response?.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('role', response.role);
        if (response.role === 'event') {
          localStorage.setItem('sub_domain', form.username.trim());
        }

        const home = HOME_BY_ROLE[myRole()];
        if (home) {
          window.location.href = home;
          return; // keep the button in its loading state while the portal loads
        }
        setErrorMsg('Your account does not have access to this portal. Please contact an administrator.');
      } else {
        setErrorMsg(response?.error || 'Unable to sign in. Please check your credentials.');
      }
    } catch {
      setErrorMsg('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <main className="login-page">
      <div className="login-panel">
        <div className="login-brand">
          <img src={logo} width="220" alt="Scientex Conferences" />
        </div>

        <div className="login-card">
          <h1 className="login-card__title">Sign in</h1>
          <p className="login-card__subtitle">Welcome to the Scientex Conferences admin portal.</p>

          {sessionExpired && !errorMsg && (
            <div className="alert alert-warning" role="status">Your session has expired. Please sign in again.</div>
          )}
          {errorMsg && <div className="alert alert-danger" role="alert">{errorMsg}</div>}

          <form onSubmit={submit} noValidate>
            <div className="mb-3">
              <label htmlFor="login-username" className="form-label">
                Username <span className="text-danger" aria-hidden="true">*</span>
              </label>
              <div className="input-icon">
                <LuUser className="input-icon__glyph" aria-hidden="true" />
                <input
                  id="login-username"
                  type="text"
                  name="username"
                  className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                  value={form.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                  autoComplete="username"
                  autoFocus
                  required
                  aria-invalid={Boolean(errors.username)}
                  aria-describedby={errors.username ? 'login-username-error' : undefined}
                />
              </div>
              {errors.username && <div id="login-username-error" className="invalid-feedback d-block">{errors.username}</div>}
            </div>

            <div className="mb-2">
              <label htmlFor="login-password" className="form-label">
                Password <span className="text-danger" aria-hidden="true">*</span>
              </label>
              <div className="input-icon">
                <LuLock className="input-icon__glyph" aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className={`form-control input-icon__field--trailing ${errors.password ? 'is-invalid' : ''}`}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? 'login-password-error' : undefined}
                />
                <button
                  type="button"
                  className="input-icon__action"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
                </button>
              </div>
              {errors.password && <div id="login-password-error" className="invalid-feedback d-block">{errors.password}</div>}
            </div>

            <p className="form-text mb-4">Forgot your password? Contact an administrator to reset it.</p>

            <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm text-white" aria-hidden="true" />
                  Signing in…
                </>
              ) : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="login-footer">&copy; {new Date().getFullYear()} Scientex Conferences</p>
      </div>
    </main>
  );
}

export default Login;
